/**
 * Command palette types (Sprint 3 — Cmd-K).
 *
 * One uniform {@link Command} shape across every category (nav / create /
 * prompt / action / system / recent). Sources are composed by
 * {@link useCommandIndex}; the palette ranks and renders the unified list.
 */

export type CommandKind = "nav" | "create" | "prompt" | "action" | "system" | "recent";

/** Primitives with a reusable standalone create modal the palette can open. */
export type CreateKind = "agent" | "folder" | "task" | "page" | "table" | "routine";

/** An MCP prompt as returned by `prompts/list`. */
export interface McpPrompt {
  name: string;
  description?: string;
  arguments?: McpPromptArgument[];
  /**
   * Sprint 24.10 — advisory metadata on Kuvio-blessed context bundles. Present
   * only on bundle entries (`kuvio_blessed: true`); powers the Settings →
   * "Context bundles" section.
   */
  _meta?: {
    kuvio_blessed?: boolean;
    estimated_tokens?: number;
    cache_ttl_seconds?: number;
  };
}

export interface McpPromptArgument {
  name: string;
  description?: string;
  required?: boolean;
}

/**
 * Cross-cutting capabilities handed to the command sources. Hook-local
 * capabilities (theme toggle, rail toggle, sign-out) are captured by each
 * source's own closure — only the shared plumbing lives here.
 */
export interface CommandContext {
  /** The "current" node derived from the route — an agent for /agents/:id, a
   *  page/memory for /library/:id. Falls back to the workspace root agent so
   *  context-aware creates always have a valid parent/owner. */
  currentNodeId: string | null;
  currentNodeType: string | null;
  currentNodeName: string | null;
  /** Workspace root agent id — the universal fallback parent/owner. */
  rootNodeId: string | null;
  navigate: (to: string) => void;
  /** Open a create modal, defaulting parent/owner to the current node. */
  openCreate: (kind: CreateKind) => void;
  /** Run an MCP prompt: collect required args, then show the composed text. */
  runPrompt: (prompt: McpPrompt) => void;
  /** Switch the palette into cheatsheet mode. */
  openCheatsheet: () => void;
  /** Sprint 17 — open the "Copy MCP endpoint URL" picker. */
  openCopyEndpoint: () => void;
  /** Close the palette. */
  close: () => void;
}

export interface Command {
  /** Stable, unique id — also the recency key and the cmdk item value. */
  id: string;
  label: string;
  /** Tabler icon name without the `ti-` prefix. */
  icon?: string;
  kind: CommandKind;
  /** Breadcrumb / target / args / recency, shown lighter under the label. */
  subtitle?: string;
  /** Extra match terms beyond the label (node path, synonyms). */
  keywords?: string[];
  /** True when behaviour depends on the current route context. */
  contextAware?: boolean;
  /** Zero-arg thunk — closes over its source's context + hooks. */
  execute: () => void;
}

export const KIND_LABEL: Record<CommandKind, string> = {
  nav: "Go to",
  create: "Create",
  prompt: "Prompt",
  action: "Action",
  system: "System",
  recent: "Recent",
};

/** Icon colour per kind (S3.17 — "kind-coloured" icon). */
export const KIND_COLOR: Record<CommandKind, string> = {
  nav: "var(--c-blue-600)",
  create: "var(--c-green-600)",
  prompt: "var(--c-purple-600)",
  action: "var(--color-text-secondary)",
  system: "var(--color-text-tertiary)",
  recent: "var(--c-amber-600)",
};
